/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';

export const TERRIFIC_SDK_URL = 'https://terrific.live/terrific-sdk.js';
const TERRIFIC_SDK_SCRIPT_ID = 'terrific-sdk';

/**
 * Injects the Terrific SDK once per document. The SDK reads the Store ID from its own <script>
 * tag (`storeId` attribute), then watches the DOM with a MutationObserver and mounts every
 * `[data-source="terrific"]` container it finds — including ones added later by client-side
 * navigations, so it never needs re-initialising.
 */
export function useTerrificSdk(storeId: string | undefined) {
    useEffect(() => {
        if (!storeId || document.getElementById(TERRIFIC_SDK_SCRIPT_ID)) return;

        const script = document.createElement('script');
        script.id = TERRIFIC_SDK_SCRIPT_ID;
        script.src = TERRIFIC_SDK_URL;
        script.async = true;
        script.setAttribute('storeId', storeId);
        document.body.appendChild(script);
    }, [storeId]);
}
